import { GridRenderCellParams, isAutogeneratedRow } from '@mui/x-data-grid-premium';
import { RiAlertLine } from '@remixicon/react';
import { PropsWithChildren, ReactNode } from 'react';

import ExpandMore from 'src/components/atoms/grid/ExpandMore';
import Link from 'src/components/atoms/Link';
import { AgText, AgTooltip } from 'src/components/ui';
import { dateCellFormatter } from 'src/utils/grid/cell/cellFormatters';

export function linkCell(routeProvider, options: { before?: (params) => ReactNode; prefix?: string } = {}) {
  return (params: GridRenderCellParams) => {
    const { before, prefix = '' } = options;

    if (!params.value) {
      return '--';
    }

    return (
      <>
        {before && before(params)}
        <Link {...routeProvider(params)} label={prefix + params.formattedValue} />
      </>
    );
  };
}

export function quantityCell(onClick: (params: GridRenderCellParams) => void, errorMessage: string) {
  return (params: GridRenderCellParams) => {
    const error = params.value <= 0;

    return (
      <AgTooltip content={error && errorMessage}>
        <div className="flex gap-2 items-center">
          <Link
            fontSize="14px"
            color={error ? 'error.main' : 'primary.main'}
            onClick={() => onClick(params)}
            label={params.formattedValue}
          />
          {error && <RiAlertLine size={14} className="text-red-text" />}
        </div>
      </AgTooltip>
    );
  };
}

export function renderIgnoreGeneratedRow(render?: (params?: GridRenderCellParams) => ReactNode) {
  return (params: GridRenderCellParams) => {
    if (isAutogeneratedRow(params.row)) {
      return '';
    }

    return render ? render(params) : params.formattedValue;
  };
}

export function getterIgnoreGeneratedRow(getter: (value, row, column, apiRef) => ReactNode) {
  return (value, row, column, apiRef) => {
    if (isAutogeneratedRow(row)) {
      return undefined;
    }

    return getter(value, row, column, apiRef);
  };
}

export function formatterIgnoreGeneratedRow<_T>(formatter?: (value, row, column, apiRef) => string) {
  return (value, row, column, apiRef) => {
    if (isAutogeneratedRow(row)) {
      return undefined;
    }

    return formatter(value, row, column, apiRef);
  };
}

export function ignoreChildRows(render?: (params?: GridRenderCellParams) => ReactNode) {
  return (params: GridRenderCellParams) => {
    const { rowNode } = params;

    if (isAutogeneratedRow(params.row)) {
      return undefined;
    }

    if (rowNode.depth > 0) {
      return '--';
    }

    return render ? render(params) : params.formattedValue;
  };
}

export const erroredCell = (tooltip: string | ((value) => string), isError: (value) => boolean) => {
  return ({ value }: GridRenderCellParams) => {
    const title = typeof tooltip === 'string' ? tooltip : tooltip(value);
    const error = isError(value);

    return (
      <AgTooltip content={isError(value) && title}>
        <div className="flex gap-2 items-center">
          <AgText size="14" className={error ? 'text-red-text' : undefined}>
            {value}
          </AgText>
          {error && <RiAlertLine size={14} data-status-icon />}
        </div>
      </AgTooltip>
    );
  };
};

export function renderGroupCell(params, includeCount?) {
  const isFooter = params.id !== 'auto-generated-group-footer-root';

  return (
    <>
      <div className="mr-4">
        <ExpandMore {...params} />
      </div>
      <span>{params.value}</span>
      {includeCount && isFooter && (
        <AgText size="14" color="secondary" className="pl-1">
          ({params.rowNode.children?.length})
        </AgText>
      )}
    </>
  );
}

export function DisabledTooltip({ show, note, children }: PropsWithChildren<{ show: boolean; note: string }>) {
  if (!show) return children;
  return (
    <AgTooltip content={note} delay={500}>
      <div className="w-full h-full flex items-center">{children}</div>
    </AgTooltip>
  );
}

export function dateCellRenderer({ value }) {
  return dateCellFormatter(value);
}
